import type { FC, ReactNode } from 'react';
import React from 'react';

import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';

import { RowWithTitleContainer, SimpleBorderContainer } from '@shared/ui/containers';
import BaseFieldView from '@shared/ui/view-info/BaseFieldView';
import { MuiTooltip } from '@shared/ui-kit';

import type { IconPropertyPropsType, IPropertyViewerContainerItem } from './types';

type PropertyViewerContainerProps = {
  gridConfig?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };
  columnSpacingConfig?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };
  rowSpacingConfig?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };
  items: IPropertyViewerContainerItem;
  title?: string | ReactNode;
  titleInsideContainer?: boolean;
  children?: ReactNode;
  isShowBorder?: boolean;
  isShowBackground?: boolean;
  customWidthLeftTitle?: string;
  titlePadding?: string;
  isShowPadding?: boolean;
  paddingLeft?: string;
  customPaddingContainer?: string;
  isDetailCard?: boolean;
  isRowContainer?: boolean;
  rowTitleVariant?: string;
};
export const PropertyViewerContainer: FC<PropertyViewerContainerProps> = ({
  gridConfig = { xs: 6, sm: 6, md: 6, lg: 6 },
  columnSpacingConfig = { xs: 11, sm: 12, md: 12, lg: 12 },
  rowSpacingConfig = { xs: 11, sm: 12, md: 12, lg: 12 },
  items,
  title,
  titleInsideContainer = false,
  children,
  isShowBorder = true,
  isShowBackground = true,
  customWidthLeftTitle,
  titlePadding,
  isShowPadding = true,
  paddingLeft,
  customPaddingContainer,
  isRowContainer,
  isDetailCard,
}) => {
  const theme = useTheme();
  const isSmallDisplay = useMediaQuery(theme.breakpoints.down('md'));
  const isShowLeftTitle = !titleInsideContainer && !isSmallDisplay && title;
  const isShowTitleInsideContainer = title && (titleInsideContainer || isSmallDisplay);

  return (
    <Box display="flex" alignItems="flexStart" width="100%" position="relative">
      {isShowLeftTitle && (
        <Typography
          sx={{
            width: customWidthLeftTitle || '160px',
            flexShrink: '0',
            padding: titlePadding || '8px 0 0 0 ',
            mr: customWidthLeftTitle ? '0' : '24px',
          }}
          color={theme.palette.case.neutral.n800}
          variant="s2"
        >
          {title}
        </Typography>
      )}

      <Box sx={{ width: '100%' }}>
        <SimpleBorderContainer
          isShowBackground={isShowBackground}
          isShowBorder={isShowBorder}
          isShowPadding={isShowPadding}
          paddingLeft={paddingLeft}
          customPaddingContainer={customPaddingContainer}
        >
          {!children && (
            <Grid container rowSpacing="16px">
              {isShowTitleInsideContainer && (
                <Grid item xs={12}>
                  <Typography color={theme.palette.case.neutral.n800} variant="s2">
                    {title}
                  </Typography>
                </Grid>
              )}

              {!children && (
                <Grid item xs={12}>
                  <Grid
                    container
                    alignItems="flex-start"
                    columnSpacing={{
                      xs: `${columnSpacingConfig.xs}px`,
                      sm: `${columnSpacingConfig.sm}px`,
                      md: `${columnSpacingConfig.md}px`,
                      lg: `${columnSpacingConfig.lg}px`,
                    }}
                    rowSpacing={{
                      xs: `${rowSpacingConfig.xs}px`,
                      sm: `${rowSpacingConfig.sm}px`,
                      md: `${rowSpacingConfig.md}px`,
                      lg: `${rowSpacingConfig.lg}px`,
                    }}
                  >
                    {items.map((item, index) => (
                      <Grid
                        key={index}
                        xs={item.gridConfig?.xs ? item.gridConfig.xs : gridConfig.xs}
                        sm={item.gridConfig?.sm ? item.gridConfig.sm : gridConfig.sm}
                        md={item.gridConfig?.md ? item.gridConfig.md : gridConfig.md}
                        lg={item.gridConfig?.lg ? item.gridConfig.lg : gridConfig.lg}
                        item
                      >
                        {isRowContainer && (
                          <RowWithTitleContainer
                            titleWidth="144px"
                            variant="medium"
                            titleColor={theme.palette.case.neutral.n500}
                            titleVariant="t14r"
                            alignItems="flexStart"
                            titlePadding={4}
                            title={item.label}
                          >
                            <BaseFieldView
                              label=""
                              value={item.value}
                              startIcon={item.startIcon}
                              endIcon={item.endIcon}
                              noWrapValue={!!item.noWrapValue}
                              valueVariant="t16r"
                            />
                          </RowWithTitleContainer>
                        )}

                        {isDetailCard && !isRowContainer && (
                          <DetailsCard label={item.label} value={item.value} startIcon={item.startIcon} />
                        )}
                        {!isDetailCard && !isRowContainer && (
                          <BaseFieldView
                            label={item.label}
                            value={item.value}
                            startIcon={item.startIcon}
                            endIcon={item.endIcon}
                            noWrapValue={!!item.noWrapValue}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
          {children && isShowTitleInsideContainer && (
            <Grid container mb="16px">
              <Grid item xs={12}>
                <Typography variant="s2" color={theme.palette.case.neutral.n800}>
                  {title}
                </Typography>
              </Grid>
            </Grid>
          )}
          {children}
        </SimpleBorderContainer>
      </Box>
    </Box>
  );
};

type DetailsCardPropsType = {
  startIcon?: Partial<IconPropertyPropsType>;
  label?: string;
  value?: string | number | null;
};

const DetailsCard: FC<DetailsCardPropsType> = (props) => {
  const { startIcon, label, value } = props;

  const theme = useTheme();
  return (
    <Box
      sx={{
        padding: '12px 16px 16px 16px',
        backgroundColor: theme.palette.case.neutral.n75,
        borderRadius: '8px',
        maxHeight: '104px',
        minHeight: '104px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '6px',
        }}
      >
        <Box sx={{ svg: { width: '24px', height: '24px', path: { fill: theme.palette.case.neutral.n700 } } }}>
          {startIcon?.icon}
        </Box>
        <Typography variant="t14r" color={theme.palette.case.neutral.n500} noWrap>
          {label}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          height: '48px',
        }}
      >
        <Box
          sx={{
            overflow: 'hidden',
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
          }}
        >
          <MuiTooltip title={(value as string) ?? ''} isArrow={false}>
            <Typography variant="t16r" color={theme.palette.case.neutral.n800}>
              {value ?? '––'}
            </Typography>
          </MuiTooltip>
        </Box>
      </Box>
    </Box>
  );
};
